import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { PackItemSpec } from "../scenario/types";

export type PinScreen = {
  id: string;
  xPct: number;
  yPct: number;
  visible: boolean;
};

const DEG = Math.PI / 180;

/**
 * Direction of an equirectangular texel (u,v in 0..1) as seen from the sphere
 * centre. This is EXACT for a lat/long pano — no camera-lens guessing. A global
 * yaw offset + optional mirror reconcile our convention with three's sphere UV
 * seam and the pano mesh rotation (calibrated once; one object aligned => all).
 */
function equirectDir(
  u: number,
  v: number,
  yawOffsetDeg: number,
  mirror: number,
): THREE.Vector3 {
  const lon = (mirror * (u - 0.5) * 360 + yawOffsetDeg) * DEG;
  const lat = (0.5 - v) * 180 * DEG;
  const cosLat = Math.cos(lat);
  // yaw 0 = -Z (camera default forward).
  return new THREE.Vector3(
    cosLat * Math.sin(lon),
    Math.sin(lat),
    -cosLat * Math.cos(lon),
  ).normalize();
}

/** Legacy still-% path (kept for any scenario without measured u/v). */
function stillPctToSphere(xPct: number, yPct: number, vfovDeg: number, aspect: number) {
  const vHalf = vfovDeg / 2;
  const hHalf = (Math.atan(Math.tan(vHalf * DEG) * aspect) * 180) / Math.PI;
  return { yawDeg: ((50 - xPct) / 50) * hHalf, pitchDeg: ((50 - yPct) / 50) * vHalf };
}

function dirFromYawPitch(yawDeg: number, pitchDeg: number): THREE.Vector3 {
  const yaw = yawDeg * DEG;
  const pitch = pitchDeg * DEG;
  return new THREE.Vector3(
    Math.sin(yaw) * Math.cos(pitch),
    Math.sin(pitch),
    -Math.cos(yaw) * Math.cos(pitch),
  ).normalize();
}

/**
 * Projects pack markers onto the screen every frame so they stay glued to the
 * Marble pano while the learner looks around. Anchors are measured equirect
 * pixels of the real objects; projection uses camera.project (exact).
 */
export function PackPinProjector({
  items,
  eye = [0, 1.5, 0],
  radius = 10,
  yawOffsetDeg = 0,
  mirror = 1,
  onUpdate,
}: {
  items: PackItemSpec[];
  eye?: [number, number, number];
  radius?: number;
  yawOffsetDeg?: number;
  mirror?: number;
  onUpdate: (pins: PinScreen[]) => void;
}) {
  const { camera, size } = useThree();
  const tmp = useRef(new THREE.Vector3()).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toPin = useRef(new THREE.Vector3()).current;
  const last = useRef<PinScreen[]>([]);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useFrame(() => {
    const cam = camera as THREE.PerspectiveCamera;
    const aspect = size.width / Math.max(1, size.height);
    // Project each pin relative to the CURRENT camera position (the pano sphere
    // is also camera-anchored), so the tiny OrbitControls orbit translation adds
    // zero parallax — pins depend only on camera ORIENTATION and stay glued to
    // the baked pano while looking around. `eye` retained for API compat.
    void eye;
    cam.getWorldDirection(camDir);
    const out: PinScreen[] = items.map((item) => {
      let dir: THREE.Vector3;
      if (item.u != null && item.v != null) {
        dir = equirectDir(item.u, item.v, yawOffsetDeg, mirror);
      } else if (item.yawDeg != null && item.pitchDeg != null) {
        dir = dirFromYawPitch(item.yawDeg, item.pitchDeg);
      } else {
        const s = stillPctToSphere(item.xPct, item.yPct, cam.fov ?? 70, aspect);
        dir = dirFromYawPitch(s.yawDeg, s.pitchDeg);
      }
      tmp.copy(cam.position).addScaledVector(dir, radius);
      toPin.copy(dir);
      const inFront = toPin.dot(camDir) > 0.05;
      tmp.project(cam);
      const xPct = (tmp.x * 0.5 + 0.5) * 100;
      const yPct = (-tmp.y * 0.5 + 0.5) * 100;
      const onScreen = xPct > -8 && xPct < 108 && yPct > -8 && yPct < 108;
      return { id: item.id, xPct, yPct, visible: inFront && onScreen };
    });

    const prev = last.current;
    let changed = out.length !== prev.length;
    if (!changed) {
      for (let i = 0; i < out.length; i++) {
        const a = out[i];
        const b = prev[i];
        if (
          a.id !== b.id ||
          a.visible !== b.visible ||
          Math.abs(a.xPct - b.xPct) > 0.35 ||
          Math.abs(a.yPct - b.yPct) > 0.35
        ) {
          changed = true;
          break;
        }
      }
    }
    if (changed) {
      last.current = out;
      onUpdateRef.current(out);
    }
    if (typeof window !== "undefined") {
      (window as unknown as { __packPins?: PinScreen[] }).__packPins = out;
    }
  });

  return null;
}
